import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useRecordContext } from "react-admin";
import { featureFlags } from "@/utils/featureFlags";
import type { BasisWindow, Policy } from "@/types";
import { formatDate } from "@/utils/formatters/dateFormatter";
import { displayContractBasis } from "@/utils/contractBasis";

/**
 * Helper text for the two benefit date fields the contract basis extends (#7481 AC).
 * Either keying convention is correct; what a user must not do is add the months
 * themselves on top of a date that already includes them.
 */
export const INCURRED_START_HELPER =
  "Leave at the term start to let the contract basis add the run-in, or enter the contract's printed start date. Don't subtract the months yourself.";
export const PAID_END_HELPER =
  "Leave at the term end to let the contract basis add the run-out, or enter the contract's printed end date. Don't add the months yourself.";

export interface BasisWindowSummary {
  incurred: string;
  paid: string;
  /** e.g. "12 / 15 gives 3 months of run-out from the term" (what the basis gives, not what was applied) */
  basis: string;
  mismatch: boolean;
  /** Tooltip for the mismatch chip; names the bare-Paid exception when it applies. */
  mismatchHelp: string;
}

const months = (n: number) => `${n} month${n === 1 ? "" : "s"}`;
const day = (v: string | null) => (v ? formatDate(v) : "—");

/**
 * Plain-language summary of a window the engine handed back. Only labels the months the
 * view already applied (N or M beyond the quoted base, TL); the dates are the view's.
 */
export const describeBasisWindow = (w: BasisWindow): BasisWindowSummary => {
  const incurred = w.incurred_check_applies
    ? `${day(w.incurred_window_start)} – ${day(w.incurred_window_end)}`
    : `through ${day(w.incurred_window_end)} (no incurred lookback: Paid basis)`;
  const paid = `${day(w.paid_window_start)} – ${day(w.paid_window_end)}`;

  const base = w.basis_base_months ?? 12;
  const parts: string[] = [];
  if (w.basis_defaulted || !w.contract_basis) {
    parts.push("no contract basis on file, 12/12 assumed");
  } else {
    const runIn = w.incurred_months !== null && w.incurred_months < 999 ? Math.max(w.incurred_months - base, 0) : 0;
    const runOut = w.paid_months !== null ? Math.max(w.paid_months - base, 0) : 0;
    const ext: string[] = [];
    if (runIn > 0) ext.push(`${months(runIn)} of run-in`);
    if (runOut > 0) ext.push(`${months(runOut)} of run-out`);
    // 999 renders as "Paid", never the number (ADR-081 5.2a). "gives", not "applied": the
    // view applies the months only to a bound left at the term, and it does not say which.
    const label = displayContractBasis(w.contract_basis);
    parts.push(
      ext.length > 0
        ? `${label} gives ${ext.join(" and ")} from the term`
        : `${label}: no run-in or run-out beyond the term`
    );
  }
  if ((w.tl_months ?? 0) > 0) parts.push(`Terminal Liability adds ${months(w.tl_months as number)} to the paid end`);

  const mismatchHelp =
    "A keyed date is neither the term nor what the contract basis gives from it. Confirm it against the policy document: a printed date that differs from the basis is honoured exactly as keyed, a date with the months added by hand is not." +
    (w.incurred_check_applies
      ? ""
      : " On a Paid basis a keyed incurred start is ignored: there is no incurred lookback.");

  return { incurred, paid, basis: parts.join("; "), mismatch: w.basis_mismatch, mismatchHelp };
};

/**
 * Shows the accumulation window the engine will use for one basis kind, read from the
 * policy record's `deductible_basis` (never re-derived here). Renders nothing with the
 * deductible-basis flag off or when the record carries no window (older API). Not mounted on
 * the create dialog: there is no record there yet.
 */
export const EffectiveWindowNote = ({ basis }: { basis: "specific" | "aggregate" }) => {
  const record = useRecordContext<Policy>();
  const w = record?.deductible_basis?.[basis];
  if (!featureFlags.deductibleBasisViewEnabled || !w) return null;
  const d = describeBasisWindow(w);
  return (
    <Stack spacing={0.5} data-testid={`effective-window-${basis}`} sx={{ mt: 0.5, mb: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <InfoOutlinedIcon fontSize="small" color="action" />
        <Typography variant="body2">
          <strong>Effective window:</strong> incurred {d.incurred}; paid {d.paid}.
        </Typography>
        {d.mismatch && (
          <Tooltip title={d.mismatchHelp}>
            <Chip size="small" color="warning" tabIndex={0} label="Dates disagree with the contract basis" />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {d.basis}. A benefit date left at the term gets this automatically; a date that differs from the term is used as
        written, nothing is added to it. When editing a date below, leave it at the term or enter the contract&apos;s
        printed date. Don&apos;t add the months yourself.
      </Typography>
    </Stack>
  );
};
